import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import InterestForm from '@/components/landing/interest-form'
import SectionHeading from '@/components/landing/section-heading'

import {
    BIKE_IMAGES,
    FRAME_SIZES,
    OFFICIAL_PRODUCT_URL,
    PRICE
} from '@/constants/bike'

const RegisterSection: FC = (): ReactNode => {
    const t = useDictionary()
    const { register, hero } = t
    const image = BIKE_IMAGES.rearQuarter

    return (
        <section id='register' aria-labelledby='register-title' className='relative isolate overflow-hidden border-t border-border py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_50%_at_18%_45%,oklch(0.36_0.14_25/0.45),transparent_70%)]' />

            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.05fr] lg:gap-16'>
                <div>
                    <Reveal>
                        <SectionHeading id='register-title' eyebrow={register.eyebrow} title={register.title} />
                    </Reveal>

                    <Reveal delay={80}>
                        <p className='mt-6 max-w-xl text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                            {register.body}
                        </p>
                    </Reveal>

                    <Reveal delay={120}>
                        <Image
                            src={image.src}
                            width={image.width}
                            height={image.height}
                            alt={register.imageAlt}
                            sizes='(min-width: 1280px) 600px, (min-width: 1024px) 48vw, 100vw'
                            className='mt-6 h-auto w-full'
                        />
                    </Reveal>

                    <dl className='mt-2 grid grid-cols-2 gap-6 border-t border-border pt-6 sm:grid-cols-3'>
                        <div className='col-span-2 sm:col-span-1'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {hero.priceLabel}
                            </dt>
                            <dd className='mt-1 font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                {PRICE}
                            </dd>
                        </div>

                        <div>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {hero.colorLabel}
                            </dt>
                            <dd className='mt-2.5 flex items-center gap-2 text-sm font-medium text-foreground'>
                                <span aria-hidden='true' className='size-3.5 rounded-full bg-gradient-to-br from-race-bright to-race-deep ring-1 ring-white/30' />
                                {hero.colorValue}
                            </dd>
                        </div>

                        <div>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {hero.sizesLabel}
                            </dt>
                            <dd className='mt-2.5 text-sm font-medium tabular-nums text-foreground'>
                                {FRAME_SIZES.join(' · ')}
                            </dd>
                        </div>
                    </dl>

                    <a
                        href={OFFICIAL_PRODUCT_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-foreground/85 transition-colors hover:text-race-bright'
                    >
                        {register.officialLink}
                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </a>
                </div>

                <Reveal delay={100} className='lg:sticky lg:top-24 lg:self-start'>
                    <InterestForm />
                </Reveal>
            </div>
        </section>
    )
}

export default RegisterSection
